import React from "react";
import { Link, useParams } from "react-router-dom";
import SEO from "../../components/SEO";
import events, { findEventBySlug } from "../../data/events";
import cronograma, { ponentes } from "../../data/cronograma";
import asset from "../../utils/assetUrl";

// /cronograma/<sede>: a donde lleva el botón "Ver cronograma" de los
// recordatorios de un día antes (WhatsApp y correo). El slug va dentro del
// template aprobado por Meta, así que esta ruta no puede cambiar de nombre.
// Sin sede muestra las tres, para compartirla con las instituciones.

const SITE = 'https://conferenciaparacanada.com';
const TZ = 'America/Mexico_City';

// "16:30" -> "4:30 p. m."
const formatHora = (hhmm) => {
    const [h, m] = hhmm.split(':').map(Number);
    const sufijo = h < 12 ? 'a. m.' : 'p. m.';
    return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${sufijo}`;
};

const formatDia = (iso) => {
    const text = new Date(iso).toLocaleDateString('es-MX', {
        weekday: 'long', day: 'numeric', month: 'long', timeZone: TZ
    });
    return text.charAt(0).toUpperCase() + text.slice(1);
};

const formatHoraIso = (iso) =>
    new Date(iso).toLocaleTimeString('es-MX', { hour: 'numeric', minute: '2-digit', timeZone: TZ });

function Logo({ ponente }) {
    if (ponente.foto) {
        return <img className="agenda__logo agenda__logo--foto" src={asset(ponente.foto)} alt={ponente.nombre} loading="lazy" />;
    }
    if (ponente.logo) {
        return <img className="agenda__logo" src={asset(ponente.logo)} alt={ponente.nombre} loading="lazy" />;
    }
    return (
        <div className="agenda__logo agenda__logo--vacio" aria-hidden="true">
            <span>{ponente.iniciales}</span>
        </div>
    );
}

function Sede({ evento }) {
    const charlas = cronograma[evento.slug] || [];
    return (
        <section className="agenda" id={evento.slug}>
            <header className="agenda__head">
                <h2 className="agenda__city">{evento.city}</h2>
                <p className="agenda__when">
                    {formatDia(evento.start)} · {formatHoraIso(evento.start)} a {formatHoraIso(evento.end)}
                </p>
                <p className="agenda__where">{evento.venue} · {evento.street}</p>
                <Link className="agenda__cal" to={`/calendario/${evento.slug}`}>Agregar a mi calendario</Link>
            </header>

            <ol className="agenda__list">
                {charlas.map((c) => {
                    const ponente = ponentes[c.clave];
                    return (
                        <li className="agenda__item" key={`${evento.slug}-${c.hora}`}>
                            <div className="agenda__time">
                                <span className="agenda__hour">{formatHora(c.hora)}</span>
                                <span className="agenda__place">{evento.city}</span>
                            </div>
                            <Logo ponente={ponente} />
                            <div className="agenda__body">
                                <p className="agenda__speaker">{ponente.nombre}</p>
                                <h3 className="agenda__title">{c.titulo}</h3>
                                <p className="agenda__detail">{c.detalle}</p>
                            </div>
                        </li>
                    );
                })}
            </ol>
        </section>
    );
}

function Cronograma() {
    const { sede } = useParams();
    const elegido = findEventBySlug(sede);
    const mostrar = elegido ? [elegido] : events;

    return (
        <>
            <SEO
                title={elegido ? `Cronograma de conferencias en ${elegido.city} | Conferencia para Canadá` : 'Cronograma de conferencias | Conferencia para Canadá'}
                description="Temas, horarios y ponentes de las conferencias en Ciudad de México, Guadalajara y Monterrey. Elige a cuáles quieres asistir."
                keywords="conferencia para canada, cronograma, agenda, conferencias, estudiar en canada"
                ogImage={`${SITE}/assets/images/og.jpg`}
                favicon={`${SITE}/assets/images/favicon-32x32.png`}
                appleFavicon={`${SITE}/assets/images/apple-touch-icon.png`}
                canonical={`${SITE}/cronograma${elegido ? `/${elegido.slug}` : ''}`}
            />

            <div className="cronograma">
                <div style={{ backgroundImage: `url(${asset('/assets/images/hero.jpg')})` }} className="cronograma__hero nostatic">
                    <div className="container">
                        <p className="cronograma__pre">Conferencia para Canadá · Octubre 2026</p>
                        <h1>Cronograma de conferencias</h1>
                        <p className="cronograma__lead">
                            Revisa los temas, los horarios y quién da cada conferencia, y organiza tu visita
                            para no perderte las que más te interesan. Todas son gratuitas.
                        </p>
                        <nav className="cronograma__tabs" aria-label="Elige tu ciudad">
                            {events.map((e) => (
                                <Link
                                    key={e.slug}
                                    to={`/cronograma/${e.slug}`}
                                    className={`cronograma__tab${elegido && elegido.slug === e.slug ? ' is-active' : ''}`}
                                    aria-current={elegido && elegido.slug === e.slug ? 'page' : undefined}
                                >
                                    {e.city}
                                </Link>
                            ))}
                        </nav>
                    </div>
                </div>

                <div className="container cronograma__content">
                    {mostrar.map((e) => <Sede key={e.slug} evento={e} />)}
                    <p className="cronograma__note">
                        Los horarios pueden ajustarse el día del evento. Llega con tiempo: la entrada es con el
                        código QR que recibiste por correo.
                    </p>
                </div>
            </div>

            <footer className="footer nostatic" style={{ backgroundImage: `url(${asset('/assets/images/footer.jpg')})` }}>
                <div className="container">
                    <div className="row footer-contact">
                        <div className="col-12">
                            <h2 className="gradient-title">¿Dudas?</h2>
                            <p>
                                Si tienes alguna pregunta, no dudes en contactarnos a{" "}
                                <a style={{ display: 'block' }} target="_blank" rel="noreferrer" href="mailto:info@para-canada.com">
                                    info@para-canada.com
                                </a>
                            </p>
                        </div>
                    </div>
                    <div className="row footer-copy">
                        <div className="col-12">
                            <p>&copy; 2026. Todos los derechos reservados.</p>
                        </div>
                    </div>
                </div>
            </footer>
        </>
    );
}

export default Cronograma;
